import assetUtils, { Asset } from 'app-lib/assetUtils'
import { sleep } from 'app-lib/asyncUtils'
import web3Utils from 'web3-utils'
import { BaseContract, ContractTxFunctionObj } from '@0x/base-contract'
import { JSONRPCRequestPayload } from '@0x/subproviders'
import { BigNumber } from '@0x/utils'
import { TransactionReceipt, Web3Wrapper } from '@0x/web3-wrapper'
import { FulcrumProvider } from '../../../fulcrum/src/services/FulcrumProvider'
import appConfig, { chainConfigs, ChainId, isChainSupported } from '../config/appConfig'
import Web3ConnectionFactory from '../services/Web3ConnectionFactory'
import { getIconSvg } from './svgSpriteUtils'

export type AnyProvider = FulcrumProvider

/**
 * An helper to get contracts and data related to web3 connection
 * Throws if any dependency is missing.
 */
export function getDependenciesStrictRead(provider: AnyProvider) {
  const { contractsSource, web3Wrapper, currentChainId, providerEngine } = provider
  if (!contractsSource) {
    throw new Error('Missing contractsSource')
  }
  if (!web3Wrapper) {
    throw new Error('Missing web3Wrapper')
  }
  if (!currentChainId || !isChainSupported(currentChainId)) {
    throw new Error(`Wrong chainId ${currentChainId}`)
  }
  if (!providerEngine) {
    throw new Error('Missing providerEngine')
  }
  const { currentAccount } = provider
  const helperContract = contractsSource.getHelperContract()
  return {
    contractsSource,
    helperContract,
    web3Wrapper,
    chainId: currentChainId,
    chain: chainConfigs[currentChainId],
    providerEngine,
    userAddress: currentAccount,
  }
}

/**
 * An helper to get contracts and data related to web3 connection
 * - currentAccount can be '' so we dont want that
 * Throws if any dependency is missing.
 */
export function getDependenciesStrict(provider: AnyProvider) {
  const deps = getDependenciesStrictRead(provider)
  const { currentAccount } = provider
  if (!currentAccount) {
    throw new Error('Missing currentAccount')
  }
  return { ...deps, userAddress: currentAccount }
}

export function getDependenciesStrictAsset(provider: AnyProvider, asset: Asset) {
  const deps = getDependenciesStrict(provider)
  const erc20Contract = deps.contractsSource.getErc20Contract(asset)
  return { ...deps, erc20Contract }
}

/**
 * Returns transaction receipt when transaction succeeded
 * Throws error if transaction fails
 */
export async function waitForTransactionSuccess(txHash: string, web3Wrapper: Web3Wrapper) {
  return web3Wrapper.awaitTransactionSuccessAsync(txHash, 5000)
}

/**
 * Helper to get the gas estimate
 * @param task a function that calls estimateGasAsync under the hood
 *
 * Example
 * ```
 * const { gasAmount } = await getGasEstimate(() =>
 *  stakingContract.exit.estimateGasAsync({
 *    from: account,
 *    gas: gasLimit
 *  }),
 *  {coefficient, fallback}
 * )
 * ```
 */
export async function getGasEstimate(
  task: () => Promise<number>,
  gasConfig: { coefficient?: number; fallback?: number }
): Promise<BigNumber> {
  let gasAmountBN
  try {
    const gasAmount = await task()
    gasAmountBN = new BigNumber(gasAmount)
      .times(gasConfig.coefficient || 1)
      .integerValue(BigNumber.ROUND_UP)
  } catch (err) {
    console.error('Failed gas estimate', err)
    gasAmountBN = new BigNumber(gasConfig.fallback || appConfig.gas.gasLimitFallback)
  }
  return gasAmountBN
}

/**
 * Generic generator to run function calls (sendTransactionAsync)
 * Optionally pass the amount of native token to send with the tx (ETH, BNB, MATIC)
 */
export async function* runTx(
  provider: AnyProvider,
  tx: ContractTxFunctionObj<any>,
  contract: BaseContract, // Passing the contract instance is only for debug purposes
  nativeAmountInBaseUnit?: BigNumber
) {
  const { userAddress, web3Wrapper, chainId } = getDependenciesStrict(provider)

  // @ts-ignore
  if (typeof debugTx === 'boolean' && debugTx === true) {
    // Allow to print TX data for debugging. Requires global.debugTx to be defined.
    const token = assetUtils.getAssetFromAddress(chainId, contract.address)
    const debugInfo: any = {
      contractName: contract.contractName,
      contractAddress: contract.address,
      encodedTxData: tx.getABIEncodedTransactionData(),
    }
    if (token !== Asset.UNKNOWN) {
      debugInfo.token = token
    }
    console.log(JSON.stringify(debugInfo, null, 2))
  }

  const gasAmount = await getGasEstimate(
    () => tx.estimateGasAsync({ from: userAddress, ...{ value: nativeAmountInBaseUnit } }),
    chainConfigs[chainId].gas.amount
  )
  yield { gasAmount }

  const hash = await tx.sendTransactionAsync({
    from: userAddress,
    gas: gasAmount,
    ...{ value: nativeAmountInBaseUnit },
  })
  yield { gasAmount, hash }

  const receipt = await waitForTransactionSuccess(hash, web3Wrapper)
  yield { gasAmount, hash, receipt }
}

/**
 * Note: the amount returned is in wei; not divided by its token precision
 */
function getEthBalance(provider: AnyProvider): Promise<BigNumber> {
  const { web3Wrapper, userAddress } = getDependenciesStrict(provider)
  return web3Wrapper.getBalanceInWeiAsync(userAddress)
}

/**
 * returns amount in base unit
 */
function getErc20Balance(
  provider: AnyProvider,
  contractAddress: string,
  holderAddress?: string
): Promise<BigNumber> {
  const account = holderAddress || provider.currentAccount
  if (!account) {
    throw new Error('Missing account')
  }
  const { contractsSource } = getDependenciesStrictRead(provider)
  const tokenContract = contractsSource.getErc20Contract(contractAddress)
  return tokenContract.balanceOf(account).callAsync()
}

/**
 * @param amount eg: want to send 10 USDC; BigNumber(10)
 */
function sendErc20(provider: AnyProvider, token: Asset | string, to: string, amount: BigNumber) {
  const { contractsSource, chainId } = getDependenciesStrict(provider)
  const address = web3Utils.isAddress(token)
    ? token
    : assetUtils.getAddress(chainId, token as Asset)

  const tokenContract = contractsSource.getErc20Contract(address)
  let decimals = 18

  if (web3Utils.isAddress(token)) {
    // TODO fetch decimals onchain
  } else {
    decimals = assetUtils.getDecimals(provider.currentChainId, token as Asset)
  }
  const amountInBaseUnit = amount.times(10 ** decimals)
  const tx = tokenContract.transfer(to, amountInBaseUnit)
  return runTx(provider, tx, tokenContract)
}

/**
 * Returns the balance of a token in human-readable amount result.
 */
async function getTokenBalanceOfUser(
  provider: AnyProvider,
  asset: Asset,
  account?: string
): Promise<BigNumber> {
  let result: BigNumber = new BigNumber(0)
  if (asset === Asset.UNKNOWN) {
    return new BigNumber(0)
  } else if (assetUtils.isNativeTokenForNetwork(provider.currentChainId, asset)) {
    return (await getEthBalance(provider)).div(10 ** 18)
  } else {
    const currentAccount = account ?? provider.currentAccount
    // get erc20 token balance
    const precision = assetUtils.getDecimals(provider.currentChainId, asset)
    const assetErc20Address = assetUtils.getAddress(provider.currentChainId, asset)
    result = await getErc20Balance(provider, assetErc20Address, currentAccount)
    result = result.div(10 ** precision)
  }
  return result
}

async function getITokenBalanceOfUser(provider: AnyProvider, asset: Asset): Promise<BigNumber> {
  const { chainId } = getDependenciesStrict(provider)
  let result = new BigNumber(0)
  const precision = assetUtils.getDecimals(chainId, asset.replace('v1', '') as Asset)
  const address = assetUtils.getITokenAddress(chainId, asset)
  result = await getErc20Balance(provider, address)
  return result.div(10 ** precision)
}

const waitForTransactionMined = async (
  provider: AnyProvider,
  txHash: string
): Promise<TransactionReceipt> => {
  const { web3Wrapper } = getDependenciesStrict(provider)
  return waitForTransactionSuccess(txHash, web3Wrapper)
}

const getLocalstorageItem = (item: string) => {
  let response = ''
  try {
    response = localStorage.getItem(item) || ''
  } catch (e) {
    console.error(e)
  }
  return response
}

const setLocalstorageItem = (item: string, val: string) => {
  try {
    localStorage.setItem(item, val)
  } catch (e) {
    console.error(e)
  }
}

const getCurrentAccount = (provider: AnyProvider): string => {
  return provider.impersonateAddress
    ? provider.impersonateAddress
    : provider.accounts.length > 0 && provider.accounts[0]
    ? provider.accounts[0].toLowerCase()
    : ''
}

/**
 * Get balances of the user in any wallet. Balances are already divided by precision.
 * @param provider
 * @param userAddress user wallet
 * @param assets a list of assets (tokens) we want to know the balance
 */
async function getWalletBalances(provider: AnyProvider, userAddress: string, assets: Asset[]) {
  const { helperContract, chainId } = getDependenciesStrictRead(provider)
  const chain = chainConfigs[chainId]
  const tokens = assets.filter((asset) => asset !== chain.native.asset)

  const { addresses, decimals } = tokens.reduce(
    (acc, asset) => {
      const { address, decimals } = assetUtils.getChainDetails(chainId, asset)
      acc.addresses.push(address)
      acc.decimals.push(decimals)
      return acc
    },
    { addresses: [] as string[], decimals: [] as number[] }
  )

  const balances = await helperContract.balanceOf(addresses, userAddress).callAsync()

  const walletBalances = balances.map((balance, index) => ({
    token: tokens[index],
    amount: balance.div(10 ** decimals[index]),
  }))

  if (tokens.length !== assets.length) {
    // Meaning that the native asset was in the list, we need to get its balance differently
    const nativeAmount = await getTokenBalanceOfUser(provider, chain.native.asset)
    walletBalances.push({ token: chain.native.asset, amount: nativeAmount })
  }

  return walletBalances
}

function inBaseUnits(chainId: ChainId, asset: Asset, amount: BigNumber) {
  const decimals = assetUtils.getDecimals(chainId, asset)
  return amount.times(10 ** decimals)
}

/**
 * helper to add token to user wallet for Metamask
 */
function addToken(chainId: ChainId, asset: Asset) {
  const assetInfo = assetUtils.getInfo(chainId, asset)
  const provider = Web3ConnectionFactory.providerEngine
  const svgLogo = getIconSvg(assetUtils.getCommon(asset).spriteLogo)
  const image = svgLogo ? 'data:image/svg+xml,' + escape(svgLogo) : undefined
  // @ts-ignore
  const payload = {
    method: 'wallet_watchAsset',
    params: {
      type: 'ERC20', // Initially only supports ERC20, but eventually more!
      options: {
        address: assetInfo.address, // The address that the token is at.
        symbol: asset, // A ticker symbol or shorthand, up to 5 chars.
        decimals: assetInfo.decimals, // The number of decimals in the token
        image, // A string url of the token logo
      },
    },
  } as JSONRPCRequestPayload

  provider.request(payload)
}

function wrapNativeToken(provider: AnyProvider, amount: BigNumber) {
  const { contractsSource } = getDependenciesStrict(provider)
  const wrappedNative = contractsSource.getWrappedNativeContract()
  const tx = wrappedNative.deposit()
  return runTx(provider, tx, wrappedNative, amount.times(10 ** 18)) // Assuming all natives are 18 decimals
}

function unwrapNativeToken(provider: AnyProvider, amount: BigNumber) {
  const { contractsSource } = getDependenciesStrict(provider)
  const wrappedNative = contractsSource.getWrappedNativeContract()
  const tx = wrappedNative.withdraw(amount.times(10 ** 18)) // Assuming all natives are 18 decimals
  return runTx(provider, tx, wrappedNative)
}

export default {
  addToken,
  getTokenBalanceOfUser,
  getCurrentAccount,
  getDependenciesStrict,
  getDependenciesStrictAsset,
  getDependenciesStrictRead,
  getErc20Balance,
  getEthBalance,
  getITokenBalanceOfUser,
  getLocalstorageItem,
  getWalletBalances,
  inBaseUnits,
  runTx,
  sendErc20,
  setLocalstorageItem,
  sleep,
  unwrapNativeToken,
  waitForTransactionMined,
  waitForTransactionSuccess,
  wrapNativeToken,
}
